import React, { useCallback, useMemo } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import type {
  SavedSearchGroup,
  SavedSearchItem,
} from 'talent_search/shared/types';
import { toSavedSearchQueryString } from 'talent_matching/components/filters/utils';
import SavedFilterButtons from 'talent_matching/components/filters/saved_filter_buttons';
import LoadFiltersModal from 'talent_matching/talent_rediscovery/components/filters/load_filters_modal';
import SaveFiltersModal from 'talent_search/talent_rediscovery/modals/save_filters_modal';

export type SavedFiltersProps = {
  savedFilterSet: SavedSearchItem | null;
  setSavedFilterSet: (item: SavedSearchItem | null) => void;
  formDefaults: Record<string, unknown>;
  loadOptions: SavedSearchGroup[];
  fetchAndParseSavedSearch: (
    option: SavedSearchItem
  ) => Promise<Record<string, unknown>>;
  createSavedSearch: (
    name: string,
    queryString: string
  ) => Promise<{ id: number; label: string }>;
  updateSavedSearch?: (queryString: string) => Promise<void>;
  buildQueryString?: () => string;
};

const SavedFilters = ({
  savedFilterSet,
  setSavedFilterSet,
  formDefaults,
  loadOptions,
  fetchAndParseSavedSearch,
  createSavedSearch,
  updateSavedSearch,
  buildQueryString,
}: SavedFiltersProps) => {
  const { reset, getValues, setValue } = useFormContext();

  const filterNames = useMemo(
    () => loadOptions.flatMap((g) => g.items.map((i) => i.label)),
    [loadOptions]
  );
  const [loadModalOpen, openLoadModal, closeLoadModal] = useModal();
  const [saveModalOpen, openSaveModal, closeSaveModal] = useModal();

  const queryString = useCallback(
    () => buildQueryString?.() ?? toSavedSearchQueryString(getValues()),
    [buildQueryString, getValues]
  );

  const handleLoadSelect = useCallback(
    async (option: SavedSearchItem | null) => {
      setSavedFilterSet(option);
      if (!option) {
        reset(formDefaults);
        return;
      }

      const formFields = await fetchAndParseSavedSearch(option);
      reset(formDefaults);
      Object.keys(formFields).forEach((key) => {
        setValue(key, formFields[key], { shouldDirty: true });
      });
    },
    [setSavedFilterSet, formDefaults, fetchAndParseSavedSearch, reset, setValue]
  );

  const handleSaveFilters = useCallback(
    async (name: string) => {
      const { id, label } = await createSavedSearch(name, queryString());
      setSavedFilterSet({ id, label });
      closeSaveModal();
    },
    [createSavedSearch, queryString, setSavedFilterSet, closeSaveModal]
  );

  const handleUpdateFilters = useCallback(async () => {
    if (!updateSavedSearch) {
      return;
    }
    await updateSavedSearch(queryString());
  }, [updateSavedSearch, queryString]);

  return (
    <>
      <SavedFilterButtons
        savedFilterSet={savedFilterSet}
        onLoadFilters={openLoadModal}
        onSaveFilters={openSaveModal}
        onUpdateFilters={updateSavedSearch ? handleUpdateFilters : undefined}
      />
      <LoadFiltersModal
        isOpen={loadModalOpen}
        onDismiss={closeLoadModal}
        options={loadOptions}
        onSelect={handleLoadSelect}
      />
      <SaveFiltersModal
        isOpen={saveModalOpen}
        close={closeSaveModal}
        onSave={handleSaveFilters}
        filterNames={filterNames}
      />
    </>
  );
};

export default SavedFilters;
